import axios from "axios";

// REACT_APP_BACKEND_URL se inyecta en tiempo de build via --build-arg en el Dockerfile.
// Fallback a la URL de producción para evitar que apunte a localhost si la variable
// no fue pasada correctamente durante el build.
const BACKEND_URL =
    process.env.REACT_APP_BACKEND_URL ||
    "https://api.licitx.cl";

if (!process.env.REACT_APP_BACKEND_URL) {
    console.warn(
        "[LicitX] REACT_APP_BACKEND_URL no está definida en el build. " +
        "Usando fallback: https://api.licitx.cl. " +
        "Reconstruir la imagen pasando --build-arg REACT_APP_BACKEND_URL=<url>."
    );
}

export const API = `${BACKEND_URL}/api`;

const USER_KEY = "procurex_user";

/* -----------------------------------------------------------------------------
 * Token storage strategy
 * -----------------------------------------------------------------------------
 * Authentication tokens are transported via an httpOnly cookie issued by the
 * backend at /auth/login. The browser handles the cookie automatically as long
 * as requests carry `withCredentials: true`. Tokens never touch JavaScript and
 * are immune to XSS exfiltration.
 *
 * The only thing we persist client-side is the *public* user object (name,
 * email, role, tenant). This is purely a UX cache so the SPA can paint the
 * shell without an extra `/auth/me` round-trip on every reload. It contains
 * no secrets.
 *
 * For dev/tooling, the /auth/login response still includes `access_token` in
 * the body so curl/scripts can authenticate via `Authorization: Bearer ...`.
 * --------------------------------------------------------------------------- */

export const clearAuth = () => {
    try {
        localStorage.removeItem(USER_KEY);
    } catch (err) {
        console.debug("api: clearAuth failed", err?.message || err);
    }
};

export const readCachedUser = () => {
    try {
        const raw = localStorage.getItem(USER_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (err) {
        console.debug("api: cached user read failed", err?.message || err);
        return null;
    }
};

export const writeCachedUser = (user) => {
    try {
        if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
        else localStorage.removeItem(USER_KEY);
    } catch (err) {
        console.debug("api: cached user write failed", err?.message || err);
    }
};

export const api = axios.create({
    baseURL: API,
    timeout: 30000,
    withCredentials: true, // send/receive the httpOnly auth cookie
});

api.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err?.response?.status === 401) {
            const path = window.location.pathname;
            if (!path.startsWith("/login")
                && !path.startsWith("/forgot-password")
                && !path.startsWith("/reset-password")) {
                clearAuth();
                window.location.href = "/login";
            }
        }
        return Promise.reject(err);
    }
);

export const fmtCLP = (n) => {
    if (n === null || n === undefined) return "—";
    try {
        return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);
    } catch {
        return `$${Math.round(n).toLocaleString("es-CL")}`;
    }
};
export const fmtUF = (n) => {
    if (n === null || n === undefined) return "—";
    return `${n.toLocaleString("es-CL", { maximumFractionDigits: 0 })} UF`;
};

// Zona horaria oficial de Chile Continental.
// Se especifica explícitamente para que la hora sea correcta independientemente
// de la zona horaria del servidor/contenedor (que puede estar en UTC).
const CL_TZ = "America/Santiago";

export const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        // Tomar solo la parte de fecha para evitar desfase de zona horaria
        const clean = String(iso).split("T")[0];
        const parts = clean.split("-");
        if (parts.length === 3 && parts[0].length === 4) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: CL_TZ });
    } catch { return iso; }
};
export const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        const clean = String(iso).split("T")[0];
        const parts = clean.split("-");
        const datePart = (parts.length === 3 && parts[0].length === 4)
            ? `${parts[2]}-${parts[1]}-${parts[0]}`
            : clean;
        if (String(iso).includes("T")) {
            const d = new Date(iso);
            if (!Number.isNaN(d.getTime())) {
                const time = d.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit", timeZone: CL_TZ });
                return `${datePart} ${time}`;
            }
        }
        return datePart;
    } catch { return iso; }
};

export const daysUntil = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        const now = new Date();
        const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
        return diff;
    } catch { return null; }
};
